import React from 'react';

import { aiLanguageMessages } from '@atlaskit/editor-common/ai-messages';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import { TranslateIcon } from '../../../../icons/prompt/translate';
import type { ConvoAiFeatureId } from '../../../../utils/request/types';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import type { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemWithOptions,
	EditorPluginAIConfigItem,
} from '../../config-items';
import {
	getSortedSupportedTranslationLanguages,
	isRefinementSupport,
} from '../../utils/translation-utils';

const supportedTranslationLanguages = getSortedSupportedTranslationLanguages();

export const createTranslationConfigItems = () => {
	let featureId: ConvoAiFeatureId = 'translate';
	let adfPrompt = false;
	if (
		expValEqualsNoExposure('platform_editor_ai_non_iw_adf_streaming', 'cohort', 'adf_gpt41mini')
	) {
		featureId = 'translate_adf_gpt41mini';
		adfPrompt = true;
	} else if (
		expValEqualsNoExposure(
			'platform_editor_ai_non_iw_adf_streaming',
			'cohort',
			'adf_gemini25flash',
		) ||
		fg('platform_editor_ai_updated_adf_prompts') ||
		fg('platform_editor_ai_updated_adf_prompts_v111')
	) {
		adfPrompt = true;
		featureId = 'translate_adf';
	}

	const translationConfigItems = supportedTranslationLanguages
		.map((language) => {
			const lang = aiLanguageMessages[language];
			if (!lang) {
				return false;
			}
			const configItem: EditorPluginAIConfigItem = {
				key: `Translate to ${language}` as CONFIG_ITEM_KEYS,
				title: aiLanguageMessages[`${language}Title`],
				description: aiLanguageMessages[`${language}Description`],
				icon: () => <TranslateIcon />,
				showInRefineDropdown: isRefinementSupport(language),
				nestingConfig: {
					id: 'translate-to',
					parentTestId: 'translate-to',
					parentTitle: aiLanguageMessages.nestingParentTitlePostGA,
					shortTitle: lang,
					icon: () => <TranslateIcon />,
				},
				intentSchemaId: 'translate_intent_schema.json',
				featureId,
				adfPrompt,
				requiresTextualContent: adfPrompt,
				lang: lang.defaultMessage,
				actions: {
					empty: [
						createInsertBelow({ appearance: 'secondary' }),
						createReplace({ appearance: 'primary' }),
					],
					selection: [
						createInsertBelow({ appearance: 'secondary' }),
						createReplace({ appearance: 'primary' }),
					],
				},
			};

			return [language, configItem];
		})
		.filter(Boolean) as [string, EditorPluginAIConfigItem][];

	return Object.fromEntries(translationConfigItems);
};

export const translationConfigItems: () => EditorPluginAIConfigItem[] = () =>
	Object.values(createTranslationConfigItems());

export const translationConfigItemWithOptions: () => EditorPluginAIConfigItemWithOptions[] = () =>
	translationConfigItems().map((config) => ({
		config,
		triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
	}));

export const translationConfigItemWithOptionsEnabledForSelectionOnly: () => EditorPluginAIConfigItemWithOptions[] =
	() =>
		translationConfigItems().map((config) => ({
			config,
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		}));
